import React, { useState } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import isEmpty from 'lodash/isEmpty'
import isPlainObject from 'lodash/isPlainObject'
import { QuantityInput, QuantityDiff, PriceDisplay, Text, SubscriptionTypeLabel, DeemphasizedLink } from '@fielded/shared-ui'
import { DIRECT_ORDER_TYPES_HUMANIZED } from '@fielded/fs-api/lib/allocation/config'
import { batchNoFromId } from '@fielded/fs-api/lib/batch/tools'

import CountCheckmark from '../common/CountCheckmark'
import CountReasonsModal from './CountReasonsModal'

const CountProduct = ({
  id,
  name,
  picked,
  total,
  isChecked,
  hasAdjustment,
  paymentType,
  originalTotal,
  hideManifest,
  ledgerQuantity,
  unitPrice,
  country,
  history,
  locationId,
  serviceId,
  translatedProductId,
  showLedgerLink,
  isPsmBased,
  dontShowDiff,
  batchId,
  onToggleChecked,
  needsAdjustmentReasons,
  isFpUser = false
}) => {
  const [checked, setChecked] = useState(isChecked)
  const [quantity, setQuantity] = useState(picked)
  const [modalVisible, setModalVisibility] = useState(false)

  const updateCount = (checked, adjustmentReasons) => {
    onToggleChecked(batchId, parseInt(quantity, 10), !isChecked, paymentType, adjustmentReasons)
  }

  const handleCheck = (adjustmentReasons) => {
    const newChecked = !checked
    setChecked(newChecked)
    handleCloseModal()
    if (isPlainObject(adjustmentReasons) && !isEmpty(adjustmentReasons)) {
      updateCount(newChecked, adjustmentReasons)
    } else {
      updateCount(newChecked)
    }
  }

  const handleTryCheck = () => {
    const isQuantityChanged = parseInt(quantity, 10) !== picked
    if (!checked && needsAdjustmentReasons && isQuantityChanged) {
      setModalVisibility(true)
    } else {
      handleCheck()
    }
  }

  const handleCloseModal = () => {
    setModalVisibility(false)
  }

  const highlightInput = (event) => event.target.select()

  const totalToUse = originalTotal || total
  const difference = picked - totalToUse
  const isMatching = difference === 0
  const batchNo = batchNoFromId(id)

  return (
    <div className={classnames(
      'count-product',
      {'count-product--adjustments': hasAdjustment},
      {'count-product--is-checked': !isChecked && hasAdjustment}
    )}>
      <CountCheckmark
        id={id}
        checked={isChecked}
        onChange={handleTryCheck}
        className='count-product__check'
      />
      <div className='count-product__info'>
        <div className='count-product__name'>
          {name}
          {isPsmBased && (
            <div className='count-product__batch'>
              <span className='vs-u-row vs-u-gap-quarter'>
                <Text>Batch no:</Text>
                <Text bold>{batchNo}</Text>
              </span>
            </div>
          )}
          {!isPsmBased && (
            <div className='count-product__ledger-container'>
              {DIRECT_ORDER_TYPES_HUMANIZED[paymentType] && (
                <SubscriptionTypeLabel size='small' subscriptionType={paymentType} />
              )}
              {(typeof ledgerQuantity !== 'undefined') && (
                <Text bold size='standard' className='count-product__ledger'> Ledger qty: {ledgerQuantity} </Text>
              )}
            </div>
          )}
        </div>
        {(!hideManifest && !isPsmBased) && <div className='count-product__manifest'>
          { !dontShowDiff && <Text className='count-product__unit-price'>
            {'Unit price: '}
            <Text bold><PriceDisplay country={country} currencySymbol value={unitPrice} inColor /></Text>
          </Text>}
          {isChecked && !isMatching && !dontShowDiff && (
            <span className='count-product__diff'>
              <QuantityDiff difference={difference} />
            </span>
          )}
          <span>On manifest: {totalToUse}</span>
          <div>
            {showLedgerLink && (
              <DeemphasizedLink
                onClick={() =>
                  history.push(`/reporting/ledger/${serviceId}/${locationId}/${translatedProductId}`)
                }
                colorVariant='brand'
              >
                view ledger
              </DeemphasizedLink>
            )}
          </div>
        </div>}
      </div>
      <QuantityInput
        textAlign='right'
        value={quantity}
        onValueChange={({ value }) => setQuantity(value)}
        className='count-product__count'
        disabled={isChecked}
        onFocus={highlightInput}
        allowNegative={false}
        isAllowed={({ value }) => isFpUser ? value <= originalTotal : true}
      />
      <CountReasonsModal
        isOpen={modalVisible}
        onClickProceed={handleCheck}
        onClose={handleCloseModal}
        currentQty={quantity}
        picked={picked}
        productName={name}
      />
    </div>
  )
}

CountProduct.propTypes = {
  /**
   * Exactly one batch for the untracked product
   */
  batchId: PropTypes.string.isRequired,
  /**
   * Unique id of the product being counted, used for count check label
   */
  id: PropTypes.string,
  /**
   * Product name to display on card
   */
  name: PropTypes.string,
  /**
   * The expected total/total on manifest.
   * Used as suggested value if no count has been performed yet.
   */
  total: PropTypes.number,
  /**
   * The unit price of the product
   */
  unitPrice: PropTypes.number,
  /**
   * Confirmed count
   */
  picked: PropTypes.number,
  /**
   * Should the card be checked off?
   */
  isChecked: PropTypes.bool,
  /**
   * Runs when user checks off the count
   */
  onToggleChecked: PropTypes.func.isRequired,
  /**
   * If present, should be used instead of total prop.
   * Sourced from shipments with additional sales stats
   */
  originalTotal: PropTypes.number,
  /** Quantity of ledger in origin pack point **/
  ledgerQuantity: PropTypes.number
}

export default CountProduct
